import { Component, EventEmitter, OnDestroy, OnInit, Output, computed, inject, input, signal } from '@angular/core'
import { FormsModule } from '@angular/forms'
import { faSearch } from '@fortawesome/free-solid-svg-icons'
import { QuillEditorComponent } from 'ngx-quill'
import { Subscription } from 'rxjs'
import { MeetingHubService, MeetingParticipant, MeetingSnapshot } from 'src/app/shared/services/meeting-hub.service'
import { MeetingTimerService } from 'src/app/shared/services/meeting-timer.service'

import { BaseComponent, Meeting, MeetingPage, MeetingPageType, quillLinkHandler } from '../../shared'
import { AutoSizeDirective } from '../../shared/utility/auto-size.directive'
import { GoalsComponent } from '../goals/goals.component'
import { HeadlinesComponent } from '../headlines/headlines.component'
import { RocksComponent } from '../rocks/rocks.component'
import { ToDosComponent } from '../to-dos/to-dos.component'
import { WrapUpComponent } from '../wrap-up/wrap-up.component'

@Component({
    selector: 'app-active-meeting',
    templateUrl: './active-meeting.component.html',
    styleUrls: ['./active-meeting.component.css'],
    standalone: true,
    imports: [
        AutoSizeDirective,
        GoalsComponent,
        RocksComponent,
        HeadlinesComponent,
        ToDosComponent,
        WrapUpComponent,
        QuillEditorComponent,
        FormsModule,
    ],
})
export class ActiveMeetingComponent extends BaseComponent implements OnInit, OnDestroy {
    private timerService = inject(MeetingTimerService)
    private hub = inject(MeetingHubService)
    faSearch = faSearch
    MeetingPageType = MeetingPageType

    @Output() endMeeting = new EventEmitter<void>()

    meeting = input.required<Meeting>()
    meetings = input.required<Meeting[]>()
    pages = input.required<MeetingPage[]>()

    activePageIndex = signal(0) // default to first page

    // Live-meeting (SignalR) state
    readonly isLead = this.hub.isLead
    leadUserId = signal('')
    roster = signal<MeetingParticipant[]>([])
    leadReconnecting = signal(false)
    private hasEnded = false
    private timerBroadcastInterval: any = null
    private subscriptions: Subscription[] = []
    private whiteboardFocused = false
    private debouncedSendWhiteboard: () => void

    leadName = computed(() => {
        const lead = this.roster().find(p => p.userId.toLowerCase() === this.leadUserId().toLowerCase())
        return lead ? lead.displayName : 'Unassigned'
    })

    /** Attendees other than the current lead, used for the leadership handoff menu. */
    handoffCandidates = computed(() => this.roster().filter(p => p.userId.toLowerCase() !== this.leadUserId().toLowerCase()))

    overallMeetingTimeFormatted = computed(() => this.formatTime(this.timerService.overallMeetingTime()))
    currentPageTimeFormatted = computed(() => this.formatTime(this.timerService.currentPageTime()))
    activePage = computed(() => this.pages()[this.activePageIndex()] || null)

    editorModules = {
        toolbar: {
            container: [
                ['bold', 'italic', 'underline', 'strike'],
                ['blockquote', 'code-block'],
                [{ header: 1 }, { header: 2 }],
                [{ list: 'ordered' }, { list: 'bullet' }],
                [{ script: 'sub' }, { script: 'super' }],
                [{ indent: '-1' }, { indent: '+1' }],
                [{ direction: 'rtl' }],
                [{ size: ['small', false, 'large', 'huge'] }],
                [{ header: [1, 2, 3, 4, 5, 6, false] }],
                [{ color: [] }, { background: [] }],
                [{ font: [] }],
                [{ align: [] }],
                ['clean'],
                ['link', 'image', 'video'],
            ],
            handlers: { link: quillLinkHandler },
        },
        clipboard: {
            matchVisual: false,
        },
    }

    private debouncedSave: () => void

    constructor() {
        super()
        this.debouncedSave = this.services.utility.debounce(500, () => this.saveMeeting())
        this.debouncedSendWhiteboard = this.services.utility.debounce(300, () => this.sendWhiteboardUpdate())
    }

    ngOnInit(): void {
        this.subscribeToHubEvents()

        this.hub
            .joinMeeting(this.meeting().id)
            .then(snapshot => this.applyJoinSnapshot(snapshot))
            .catch(err => {
                console.error('Failed to join live meeting', err)
                this.services.notification.error(err?.message || 'Unable to join the live meeting.')
                this.endMeeting.emit()
            })
    }

    ngOnDestroy(): void {
        this.stopTimerBroadcast()
        this.subscriptions.forEach(s => s.unsubscribe())
        this.subscriptions = []
        this.timerService.clearMeeting()
        // Tear down the shared connection so a subsequent meeting starts clean.
        this.hub.leaveMeeting()
    }

    private subscribeToHubEvents(): void {
        this.subscriptions.push(
            this.hub.pageChanged$.subscribe(index => this.applyRemotePage(index)),
            this.hub.rosterChanged$.subscribe(update => {
                this.roster.set(update.roster)
                this.leadReconnecting.set(!!update.leadReconnecting)
                this.applyLeadChange(update.leadUserId, update.leadConnectionId)
            }),
            this.hub.leadChanged$.subscribe(update => {
                this.leadReconnecting.set(false)
                this.applyLeadChange(update.leadUserId, update.leadConnectionId)
                this.services.notification.info(`${this.leadName()} is now leading the meeting.`)
            }),
            this.hub.timerUpdated$.subscribe(timer => {
                if (!this.isLead()) this.timerService.applyRemoteSnapshot(timer)
            }),
            this.hub.meetingEnded$.subscribe(() => {
                if (this.hasEnded) return
                this.hasEnded = true
                if (!this.isLead()) this.services.notification.info('The meeting was ended by the lead.')
                this.endMeeting.emit()
            }),
            this.hub.reSynced$.subscribe(snapshot => {
                this.applyReSync(snapshot)
                this.hub.requestContentResync(this.meeting().id)
            }),
            this.hub.whiteboardChanged$.subscribe(update => {
                if (update.meetingId.toLowerCase() !== this.meeting().id.toLowerCase()) return
                if (this.whiteboardFocused) return
                this.meeting().whiteBoardContent = update.html
            }),
        )
    }

    private applyJoinSnapshot(snapshot: MeetingSnapshot): void {
        this.roster.set(snapshot.roster)
        this.leadUserId.set(snapshot.leadUserId)
        this.isLead.set(snapshot.isLead)

        // Followers mirror the lead; the lead ticks locally.
        this.timerService.initializeMeeting(this.pages().length, this.meeting().id, !snapshot.isLead)

        if (snapshot.isLead) {
            // Server page is the source of truth whenever a live session already exists
            // (other attendees present, or page already advanced). Only restore per-meeting
            // localStorage for a fresh solo session so a stale global index never gets broadcast.
            let initialIndex = snapshot.currentPageIndex
            const soloFreshSession = snapshot.roster.length <= 1 && snapshot.currentPageIndex === 0
            if (soloFreshSession) {
                const stored = localStorage.getItem(this.pageIndexStorageKey())
                if (stored !== null) {
                    const parsed = parseInt(stored, 10)
                    if (Number.isFinite(parsed)) initialIndex = parsed
                }
            }

            this.setActivePage(this.clampPageIndex(initialIndex))
            this.startTimerBroadcast()
        } else {
            const pageIndex = this.clampPageIndex(snapshot.currentPageIndex)
            this.activePageIndex.set(pageIndex)
            this.timerService.setActivePage(pageIndex)
            if (snapshot.timer) this.timerService.applyRemoteSnapshot(snapshot.timer)
            this.services.notification.info(`Joined a meeting in progress. ${this.leadName()} is leading.`)
        }
    }

    private pageIndexStorageKey(): string {
        return `activeMeetingPageIndex_${this.meeting().id}`
    }

    private clampPageIndex(index: number): number {
        const max = Math.max(0, this.pages().length - 1)
        if (!Number.isFinite(index) || index < 0) return 0
        return Math.min(Math.floor(index), max)
    }

    private applyReSync(snapshot: MeetingSnapshot): void {
        // After a reconnect the connection id is fresh, so the server-computed isLead is authoritative.
        this.roster.set(snapshot.roster)
        this.reconcileLeadRole(snapshot.leadUserId, snapshot.isLead)
        if (!this.isLead()) {
            const pageIndex = this.clampPageIndex(snapshot.currentPageIndex)
            this.activePageIndex.set(pageIndex)
            this.timerService.setActivePage(pageIndex)
            if (snapshot.timer) this.timerService.applyRemoteSnapshot(snapshot.timer)
        }
    }

    private applyLeadChange(newLeadUserId: string, newLeadConnectionId: string): void {
        // Lead-ship is tied to a specific connection (a user may have multiple tabs open).
        const amLead = !!this.hub.connectionId && newLeadConnectionId === this.hub.connectionId
        this.reconcileLeadRole(newLeadUserId, amLead)
    }

    private reconcileLeadRole(newLeadUserId: string, amLead: boolean): void {
        const wasLead = this.isLead()
        this.leadUserId.set(newLeadUserId)
        this.isLead.set(amLead)

        if (amLead && !wasLead) {
            // Promoted to lead: take over local timing and start broadcasting.
            this.timerService.assumeLocalControl(this.meeting().id)
            this.startTimerBroadcast()
        } else if (!amLead && wasLead) {
            // Lost lead: stop broadcasting and mirror the new lead instead.
            this.stopTimerBroadcast()
            this.timerService.relinquishLocalControl()
        }
    }

    /** Lead action: navigate the meeting to a page (also broadcast to followers). */
    setActivePage(index: number) {
        if (!this.isLead()) return

        const pageIndex = this.clampPageIndex(index)
        this.activePageIndex.set(pageIndex)
        this.timerService.setActivePage(pageIndex)
        localStorage.setItem(this.pageIndexStorageKey(), pageIndex.toString())

        this.hub.setActivePage(this.meeting().id, pageIndex).catch(err => {
            console.error('Failed to broadcast page change', err)
        })
        this.hub.updateTimer(this.meeting().id, this.timerService.getSnapshot()).catch(err => {
            console.error('Failed to broadcast timer', err)
        })
    }

    /** Follower path: apply a page change pushed by the lead. */
    private applyRemotePage(index: number): void {
        const pageIndex = this.clampPageIndex(index)
        this.activePageIndex.set(pageIndex)
        this.timerService.setActivePage(pageIndex)
    }

    /** Lead action: hand leadership to another connected attendee. */
    reassignLead(userId: string): void {
        if (!this.isLead()) return
        this.hub.reassignLead(this.meeting().id, userId).catch(err => {
            console.error('Failed to reassign lead', err)
            this.services.notification.error(err?.message || 'Unable to reassign the meeting lead.')
        })
    }

    /**
     * End (lead) or leave (follower) the meeting.
     * Awaits the hub EndMeeting invoke before tearing down so the connection is not stopped mid-flight.
     */
    async leaveOrEndMeeting(): Promise<void> {
        if (this.isLead()) {
            // Mark ended before the invoke so a MeetingEnded callback cannot destroy us mid-flight.
            this.hasEnded = true
            try {
                await this.hub.endMeeting(this.meeting().id)
            } catch (err: any) {
                this.hasEnded = false
                console.error('Failed to end meeting for everyone', err)
                this.services.notification.error(err?.message || 'Unable to end the meeting for everyone. Please try again.')
                return
            }
        }
        this.endMeeting.emit()
    }

    private startTimerBroadcast(): void {
        this.stopTimerBroadcast()
        this.timerBroadcastInterval = setInterval(() => {
            if (this.isLead()) {
                this.hub.updateTimer(this.meeting().id, this.timerService.getSnapshot())
            }
        }, 3000)
    }

    private stopTimerBroadcast(): void {
        if (this.timerBroadcastInterval) {
            clearInterval(this.timerBroadcastInterval)
            this.timerBroadcastInterval = null
        }
    }

    private formatTime(totalSeconds: number): string {
        const hours = Math.floor(totalSeconds / 3600)
        //const minutes = Math.floor((totalSeconds % 3600) / 60)
        const minutes = Math.floor(totalSeconds / 60)
        const seconds = totalSeconds % 60

        return `${minutes}`
        return `${this.pad(hours)}:${this.pad(minutes)}` // hours and minutes
        return `${this.pad(hours)}:${this.pad(minutes)}:${this.pad(seconds)}` // with seconds
    }

    private pad(value: number): string {
        return value < 10 ? `0${value}` : `${value}`
    }

    onWhiteBoardContentChange(content: string): void {
        this.meeting().whiteBoardContent = content
        this.debouncedSave()
        this.debouncedSendWhiteboard()
    }

    onWhiteboardFocus(): void {
        this.whiteboardFocused = true
    }

    onWhiteboardBlur(): void {
        this.whiteboardFocused = false
    }

    private sendWhiteboardUpdate(): void {
        const html = this.meeting().whiteBoardContent ?? ''
        this.hub.sendWhiteboard(this.meeting().id, html).catch(err => {
            console.error('Failed to broadcast whiteboard update', err)
        })
    }

    private saveMeeting(): void {
        this.services.data.meetings.edit(this.meeting()).subscribe({
            next: () => {
                console.log('Meeting whiteboard content saved successfully')
            },
            error: error => {
                console.error('Failed to save meeting:', error)
                this.services.notification.error('Failed to save whiteboard content')
            },
        })
    }
}
